import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import { Avatar, Modal, Spinner } from "../../components/lawyer/LawyerUi";
import { apiErrorMessage, lawyerService } from "../../services/lawyerApi";
import socketService from "../../services/SocketService";

export const fileNameOf = (url) => {
  try {
    return decodeURIComponent((url || "").split("/").pop().split("?")[0]);
  } catch {
    return (url || "").split("/").pop();
  }
};

export const fileTypeOf = (file) => {
  if (file.type.startsWith("image/")) return "image";
  if (file.type.startsWith("video/")) return "video";
  if (file.type.startsWith("audio/")) return "audio";
  return "doc";
};

const money = (v) => `$ ${Number((Number(v) || 0).toFixed(2)).toLocaleString()}`;

const sortAsc = (list) =>
  [...list].sort((a, b) => new Date(a.created_at) - new Date(b.created_at) || a.id - b.id);

// Keeps one copy per id, newest data wins.
export const mergeMessages = (current, incoming) => {
  const map = new Map(current.map((m) => [m.id, m]));
  incoming.forEach((m) => map.set(m.id, { ...map.get(m.id), ...m }));
  return sortAsc([...map.values()]);
};

export const pickRecorderMime = () => {
  if (typeof MediaRecorder === "undefined") return null;
  return ["audio/mp4", "audio/webm;codecs=opus", "audio/webm"].find((t) => MediaRecorder.isTypeSupported(t)) || "";
};

/* ---------------- Message pieces ---------------- */

const InvoiceCard = ({ message, onTaskCompleted, busy }) => {
  const inv = message.invoice_data || {};
  const paid = inv.pay_status === "Paid";
  const done = inv.task_status === "Completed";
  return (
    <div className="lwp-invoice">
      <div className="lwp-invoice-top">
        <span className="lwp-invoice-no">Invoice: #{message.id}</span>
        <span className={`lwp-status ${paid ? "lwp-status-ended" : "lwp-status-pending"}`}>
          {paid ? "Paid" : "Pending"}
        </span>
      </div>
      {inv.title && <h4>{inv.title}</h4>}
      {Array.isArray(inv.items) && inv.items.length > 0 && (
        <ul className="lwp-invoice-items">
          {inv.items.map((item, i) => (
            <li key={i}>
              <span>{item.title}</span>
              <span>{money(item.amount)}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="lwp-invoice-total">
        <strong>Total</strong>
        <span>{money(inv.total_amount)}</span>
      </div>
      {paid && (
        <div className="lwp-invoice-foot">
          {done ? (
            <span className="lwp-status lwp-status-ended">Task Completed</span>
          ) : (
            <button type="button" className="lwp-btn lwp-btn-sm" onClick={onTaskCompleted} disabled={busy}>
              {busy ? <span className="spinner-border spinner-border-sm" /> : "Mark Task Completed"}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export const Attachment = ({ message }) => {
  const { file, file_type: type } = message;
  if (!file) return null;
  if (type === "image") {
    return (
      <a href={file} target="_blank" rel="noreferrer" className="lwp-msg-media">
        <img src={file} alt="Attachment" loading="lazy" />
      </a>
    );
  }
  if (type === "video") {
    return <video className="lwp-msg-media" src={file} controls preload="metadata" />;
  }
  if (type === "audio") {
    return <audio className="lwp-msg-audio" src={file} controls preload="metadata" />;
  }
  return (
    <a href={file} target="_blank" rel="noreferrer" className="lwp-msg-doc">
      <i className="bi bi-file-earmark-text" />
      <span>{fileNameOf(file)}</span>
    </a>
  );
};

// Short one-line description of a message, used in reply quotes.
export const snippetOf = (msg) => {
  if (!msg) return "";
  const text = (msg.message || "").trim();
  if (msg.invoice_data) return `Invoice${msg.invoice_data.title ? `: ${msg.invoice_data.title}` : ""}`;
  if (msg.call_data) return "Video call";
  if (msg.file && text && text !== fileNameOf(msg.file)) return text;
  const label = { image: "Photo", video: "Video", audio: "Voice message", doc: fileNameOf(msg.file) || "Document" }[
    msg.file_type
  ];
  return label || text || "Message";
};

export const replyIcon = { image: "bi-image", video: "bi-camera-video", audio: "bi-mic", doc: "bi-file-earmark" };

export const ReplyQuote = ({ reply, author, onClick }) => (
  <button type="button" className="lwp-reply-quote" onClick={onClick} title="Go to message">
    <strong>{author}</strong>
    <span>
      {replyIcon[reply.file_type] && <i className={`bi ${replyIcon[reply.file_type]}`} />} {snippetOf(reply)}
    </span>
  </button>
);

// Record of a finished video call, posted to the chat by the backend when the call ends.
export const CallCard = ({ message }) => {
  const cd = message.call_data || {};
  const time = moment(message.created_at);
  const talk = cd.talk_time && cd.talk_time !== "—" ? cd.talk_time : "Not connected";
  const details = [cd.category, cd.jurisdiction].filter(Boolean).join(" · ");
  return (
    <div className="lwp-call-card" title={details || undefined}>
      <span className="lwp-call-card-icon">
        <i className="bi bi-telephone-inbound-fill" />
      </span>
      <div className="lwp-call-card-body">
        <strong>Video call</strong>
        <div className="lwp-call-card-row">
          <span>{talk}</span>
          <span>{time.isValid() ? time.format("hh:mm A") : ""}</span>
        </div>
      </div>
    </div>
  );
};

const MessageBubble = ({ message, mine, avatar, clientName, onTaskCompleted, busy, onReply, onJumpTo }) => {
  const text = (message.message || "").trim();
  // File-only messages carry the file name as text (the API needs a body); don't repeat it.
  const showText = text && !(message.file && text === fileNameOf(message.file)) && !message.invoice_data;
  const time = moment(message.created_at);
  const reply = message.reply_to;

  return (
    <div className={`lwp-msg${mine ? " mine" : ""}`} id={`lwp-msg-${message.id}`}>
      {!mine && <Avatar src={avatar} size={44} className="lwp-msg-avatar" />}
      <div className="lwp-msg-col">
        <div className="lwp-msg-line">
          {message.call_data ? (
            <CallCard message={message} />
          ) : message.invoice_data ? (
            <div className="lwp-msg-stack">
              {text && <div className="lwp-bubble">{text}</div>}
              <InvoiceCard message={message} onTaskCompleted={onTaskCompleted} busy={busy} />
            </div>
          ) : (
            <div className={`lwp-bubble${message.file_type === "image" || message.file_type === "video" ? " has-media" : ""}`}>
              {reply && (
                <ReplyQuote
                  reply={reply}
                  author={reply.sender === "lawyer" ? "You" : clientName}
                  onClick={() => onJumpTo(reply.id)}
                />
              )}
              <Attachment message={message} />
              {message.contract_data && (
                <div className="lwp-msg-doc">
                  <i className="bi bi-file-earmark-check" />
                  <span>
                    {message.contract_data.title} · {message.contract_data.status}
                  </span>
                </div>
              )}
              {showText && <p className="lwp-bubble-text">{text}</p>}
            </div>
          )}
          {!message.call_data && (
            <button type="button" className="lwp-reply-btn" onClick={() => onReply(message)} aria-label="Reply" title="Reply">
              <i className="bi bi-reply-fill" />
            </button>
          )}
        </div>
        <span className="lwp-msg-meta">
          {mine && !message.call_data && (
            <i
              className={`bi ${Number(message.is_read) === 1 ? "bi-check2-all read" : "bi-check2"}`}
              aria-label={Number(message.is_read) === 1 ? "Read" : "Sent"}
            />
          )}
          {time.isValid() ? time.format("hh:mm A") : ""}
        </span>
      </div>
      {mine && <Avatar src={avatar} size={44} className="lwp-msg-avatar" />}
    </div>
  );
};

/* ---------------- Invoice composer ---------------- */

const basePath = process.env.REACT_APP_BASE_PATH || "";

// commission: platform fee % deducted from the invoice total (the API applies the same rate).
const CreateInvoiceModal = ({ open, onClose, onSubmit, commission = 0 }) => {
  const [title, setTitle] = useState("");
  const [fee, setFee] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle("");
      setFee("");
      setAccepted(false);
    }
  }, [open]);

  const amount = Number(fee) || 0;
  const earn = Math.max(0, amount - (amount * commission) / 100);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return toast.error("Please enter the service title.");
    if (!(amount > 0)) return toast.error("Please enter a fee above 0.");
    if (!accepted) return toast.error("Please accept the Privacy policy and Terms & conditions.");
    const total = Number(amount.toFixed(2));
    setSaving(true);
    const ok = await onSubmit({
      title: title.trim(),
      items: [{ title: title.trim(), amount: total }],
      total_amount: total,
    });
    setSaving(false);
    if (ok) onClose();
    return undefined;
  };

  return (
    <Modal open={open} onClose={onClose} title="Create Invoice" className="lwp-invoice-modal">
      <form className="lwp-form" onSubmit={submit}>
        <label className="lwp-field">
          <span>Service Title</span>
          <input
            className="lwp-input lwp-invoice-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Service Title"
            autoFocus
          />
        </label>

        <label className="lwp-field">
          <span>Fee</span>
          <input
            className="lwp-input lwp-invoice-input"
            type="number"
            min="0"
            step="0.01"
            inputMode="decimal"
            value={fee}
            onChange={(e) => setFee(e.target.value)}
            placeholder="0"
          />
          <small className="lwp-invoice-hint">
            Platform Fee: {Number(commission) || 0}%. You'll earn {money(earn)}
          </small>
        </label>

        <div className="lwp-invoice-sum">
          <strong>Total</strong>
          <span>{money(amount)}</span>
        </div>

        <label className="lwp-check">
          <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
          <span>
            Accept all{" "}
            <a href={`${basePath}/privacy`} target="_blank" rel="noreferrer">
              Privacy policy
            </a>{" "}
            and{" "}
            <a href={`${basePath}/terms`} target="_blank" rel="noreferrer">
              Terms &amp; conditions
            </a>
            .
          </span>
        </label>

        <button type="submit" className="lwp-btn" disabled={saving}>
          {saving ? <span className="spinner-border spinner-border-sm" /> : "Submit"}
        </button>
      </form>
    </Modal>
  );
};

/* ---------------- Conversation ---------------- */

const ChatConversation = ({ chatId, onBack, onActivity }) => {
  const { profile } = useOutletContext();
  const location = useLocation();
  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [text, setText] = useState("");
  const [pendingFile, setPendingFile] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [sending, setSending] = useState(false);
  const [online, setOnline] = useState(false);
  const [typing, setTyping] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [busyInvoice, setBusyInvoice] = useState(null);
  const [recording, setRecording] = useState(null); // { seconds }

  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const inputRef = useRef(null);
  const stickToBottom = useRef(true);
  const prevScrollHeight = useRef(null);
  const typingTimer = useRef(null);
  const recorderRef = useRef(null);
  const onActivityRef = useRef(onActivity);
  onActivityRef.current = onActivity;

  const lawyerId = profile?.id;
  const userId = chat?.user_id;
  const socketChat = useCallback(
    () => ({ id: Number(chatId), user_id: userId, lawyer_id: lawyerId }),
    [chatId, userId, lawyerId]
  );

  /* ----- loading ----- */

  const loadPage = useCallback(
    async (nextPage) => {
      const res = await lawyerService.getChatMessages(chatId, nextPage);
      if (!res?.data?.status) throw new Error(res?.data?.message);
      const data = res.data.data || {};
      if (data.chat) setChat(data.chat);
      setMessages((prev) => mergeMessages(nextPage === 1 ? [] : prev, data.messages || []));
      setPage(data.pagination?.current_page || nextPage);
      setLastPage(data.pagination?.last_page || 1);
      return data;
    },
    [chatId]
  );

  useEffect(() => {
    let active = true;
    stickToBottom.current = true;
    loadPage(1)
      .catch((err) => active && toast.error(apiErrorMessage(err, err?.message || "Unable to load messages.")))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [loadPage]);

  // Arriving straight from a call: the backend writes the call record while it
  // settles the call, which can land just after our first load.
  const fromCall = location.state?.fromCall;
  useEffect(() => {
    if (!fromCall) return undefined;
    const t = setTimeout(() => loadPage(1).catch(() => {}), 2500);
    return () => clearTimeout(t);
  }, [fromCall, loadPage]);

  // Tell the client their messages were read once we know who they are.
  useEffect(() => {
    if (!userId || !lawyerId) return;
    socketService.getSocket()?.emit("message-read", { reader: "lawyer" }, socketChat());
    onActivityRef.current?.();
  }, [userId, lawyerId, socketChat]);

  const loadOlder = async () => {
    if (loadingOlder || page >= lastPage) return;
    setLoadingOlder(true);
    prevScrollHeight.current = scrollRef.current?.scrollHeight || null;
    try {
      await loadPage(page + 1);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoadingOlder(false);
    }
  };

  // Scroll handling: stay pinned to the bottom for new messages, keep position
  // when older ones are prepended.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (prevScrollHeight.current !== null) {
      el.scrollTop = el.scrollHeight - prevScrollHeight.current;
      prevScrollHeight.current = null;
    } else if (stickToBottom.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, typing]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (el.scrollTop < 60) loadOlder();
  };

  /* ----- realtime ----- */

  useEffect(() => {
    if (!userId) return undefined;
    const sameChat = (c) => c && String(c.user_id) === String(userId);
    const presence = { type: "user", id: userId };

    const offs = [
      socketService.subscribe("receive-message", (message) => {
        if (String(message?.chat_id) !== String(chatId)) return;
        stickToBottom.current = true;
        setTyping(false);
        setMessages((prev) => mergeMessages(prev, [message]));
        lawyerService.readChatMessages(chatId).catch(() => {});
        socketService.getSocket()?.emit("message-read", { reader: "lawyer" }, socketChat());
      }),
      socketService.subscribe("message-read", (data, c) => {
        if (data?.reader === "user" && sameChat(c)) {
          setMessages((prev) => prev.map((m) => (m.sender === "lawyer" ? { ...m, is_read: 1 } : m)));
        }
      }),
      socketService.subscribe("typing", (data, c) => sameChat(c) && data?.sender === "user" && setTyping(true)),
      socketService.subscribe("stop-typing", (data, c) => sameChat(c) && setTyping(false)),
      socketService.subscribe("invoice-paid", (message) => {
        if (String(message?.chat_id) === String(chatId)) setMessages((prev) => mergeMessages(prev, [message]));
      }),
      // The client changed an invoice, or marked its task done from their side.
      socketService.subscribe("invoice-updated", (message) => {
        if (String(message?.chat_id) === String(chatId)) setMessages((prev) => mergeMessages(prev, [message]));
      }),
      socketService.subscribe("task-completed", (message) => {
        if (String(message?.chat_id) === String(chatId)) setMessages((prev) => mergeMessages(prev, [message]));
      }),
      socketService.subscribe("presence-update", (p) => {
        if (p?.type === "user" && String(p.id) === String(userId)) setOnline(!!p.online);
      }),
    ];
    socketService.emit("watch-presence", presence);

    return () => {
      offs.forEach((off) => off());
      socketService.emit("unwatch-presence", presence);
    };
  }, [chatId, userId, socketChat]);

  const emitTyping = () => {
    if (!userId) return;
    const sock = socketService.getSocket();
    if (!typingTimer.current) sock?.emit("typing", { sender: "lawyer" }, socketChat());
    clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => {
      sock?.emit("stop-typing", { sender: "lawyer" }, socketChat());
      typingTimer.current = null;
    }, 1500);
  };

  useEffect(() => () => clearTimeout(typingTimer.current), []);

  /* ----- sending ----- */

  const deliver = (message) => {
    stickToBottom.current = true;
    setMessages((prev) => mergeMessages(prev, [message]));
    socketService.getSocket()?.emit("send-message", message, socketChat());
    onActivityRef.current?.(message);
  };

  const send = async (payload) => {
    setSending(true);
    try {
      const res = await lawyerService.sendMessage({ chat_id: chatId, reply_to_id: replyTo?.id, ...payload });
      if (!res?.data?.status) throw new Error(res?.data?.message);
      deliver(res.data.data);
      setReplyTo(null);
      return true;
    } catch (err) {
      toast.error(apiErrorMessage(err, err?.message || "Message not sent."));
      return false;
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const body = text.trim();
    if (sending || (!body && !pendingFile)) return;

    let ok;
    if (pendingFile) {
      const cleanName = pendingFile.name.replace(/\s+/g, "");
      ok = await send({
        file: pendingFile,
        file_type: fileTypeOf(pendingFile),
        // The API requires a text body alongside files; fall back to the file name.
        message: body || cleanName,
      });
    } else {
      ok = await send({ message: body });
    }
    if (ok) {
      setText("");
      setPendingFile(null);
      if (typingTimer.current) {
        clearTimeout(typingTimer.current);
        typingTimer.current = null;
        socketService.getSocket()?.emit("stop-typing", { sender: "lawyer" }, socketChat());
      }
      inputRef.current?.focus();
    }
  };

  const startReply = (message) => {
    setReplyTo(message);
    inputRef.current?.focus();
  };

  const jumpTo = (id) => {
    const el = document.getElementById(`lwp-msg-${id}`);
    if (!el) {
      toast.info("That message is further back. Scroll up to load older messages.");
      return;
    }
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.remove("lwp-msg-flash");
    // Restart the highlight animation even if the same message is clicked twice.
    void el.offsetWidth;
    el.classList.add("lwp-msg-flash");
    setTimeout(() => el.classList.remove("lwp-msg-flash"), 1600);
  };

  const pickFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error("Files must be 20 MB or smaller.");
      return;
    }
    setPendingFile(file);
  };

  const sendInvoice = async (invoiceData) => {
    try {
      const res = await lawyerService.sendInvoice(chatId, invoiceData);
      if (!res?.data?.status) throw new Error(res?.data?.message);
      deliver(res.data.data);
      toast.success("Invoice sent");
      return true;
    } catch (err) {
      toast.error(apiErrorMessage(err, err?.message || "Unable to send invoice."));
      return false;
    }
  };

  const markTaskCompleted = async (message) => {
    setBusyInvoice(message.id);
    try {
      const res = await lawyerService.invoiceTaskCompleted(message.id);
      if (!res?.data?.status) throw new Error(res?.data?.message);
      const updated = { ...message, ...res.data.data };
      setMessages((prev) => mergeMessages(prev, [updated]));
      socketService.getSocket()?.emit("task-completed", updated, socketChat());
      toast.success(res.data.message || "Task marked as completed");
    } catch (err) {
      toast.error(apiErrorMessage(err, err?.message || "Unable to update invoice."));
    } finally {
      setBusyInvoice(null);
    }
  };

  /* ----- voice notes ----- */

  const startRecording = async () => {
    const mime = pickRecorderMime();
    if (mime === null || !navigator.mediaDevices?.getUserMedia) {
      toast.error("Voice messages aren't supported in this browser.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      recorder.ondataavailable = (ev) => ev.data.size && chunks.push(ev.data);
      const started = Date.now();
      const tick = setInterval(() => setRecording({ seconds: Math.floor((Date.now() - started) / 1000) }), 500);
      recorderRef.current = { recorder, stream, chunks, tick, cancelled: false };
      recorder.onstop = async () => {
        clearInterval(tick);
        stream.getTracks().forEach((t) => t.stop());
        const { cancelled } = recorderRef.current || {};
        recorderRef.current = null;
        setRecording(null);
        if (cancelled || !chunks.length) return;
        const type = recorder.mimeType || mime || "audio/webm";
        const ext = type.includes("mp4") ? "m4a" : "webm";
        const name = `voice_${Date.now()}.${ext}`;
        await send({ file: new File(chunks, name, { type }), file_type: "audio", message: name });
      };
      recorder.start();
      setRecording({ seconds: 0 });
    } catch {
      toast.error("Microphone access was blocked.");
    }
  };

  const stopRecording = (cancel = false) => {
    const rec = recorderRef.current;
    if (!rec) return;
    rec.cancelled = cancel;
    rec.recorder.stop();
  };

  useEffect(
    () => () => {
      const rec = recorderRef.current;
      if (rec) {
        rec.cancelled = true;
        rec.recorder.state !== "inactive" && rec.recorder.stop();
      }
    },
    []
  );

  /* ----- service chats (clients who bought a service) ----- */

  const service = chat?.type === "service" ? chat.user_service || null : null;
  const serviceActive = !service || service.status === "Active";

  const markServiceCompleted = async () => {
    setCompleting(true);
    try {
      const res = await lawyerService.markServiceCompleted(service.id);
      if (!res?.data?.status) throw new Error(res?.data?.message);
      setChat((c) => ({ ...c, user_service: { ...c.user_service, status: "Completed" } }));
      socketService.getSocket()?.emit("service-completed", { sender: "lawyer" }, socketChat());
      toast.success(res.data.message || "Service marked as completed");
      setCompleteOpen(false);
    } catch (err) {
      toast.error(apiErrorMessage(err, err?.message || "Unable to complete the service."));
    } finally {
      setCompleting(false);
    }
  };

  /* ----- render ----- */

  const status = typing ? "typing..." : online ? "Online" : "Offline";
  const canSend = !!text.trim() || !!pendingFile;
  let lastDay = null;

  return (
    <div className={`lwp-convo${service && !serviceActive ? " readonly" : ""}`}>
      <header className="lwp-convo-head">
        <button type="button" className="lwp-back lwp-convo-back" onClick={onBack} aria-label="Back to chats">
          <i className="bi bi-chevron-left" />
        </button>
        <Avatar src={chat?.user?.picture} alt={chat?.user?.name} size={44} className="lwp-convo-avatar" />
        <div className="lwp-convo-title">
          <h2>{chat?.user?.name || " "}</h2>
          <span className={online || typing ? "is-online" : ""}>{chat ? status : ""}</span>
        </div>
        {!chat ? null : service ? (
          serviceActive && (
            <button type="button" className="lwp-pill-btn" onClick={() => setCompleteOpen(true)}>
              <i className="bi bi-check2-circle" />
              <span>Mark as Completed</span>
            </button>
          )
        ) : (
          <button type="button" className="lwp-pill-btn" onClick={() => setInvoiceOpen(true)}>
            <i className="bi bi-receipt" />
            <span>Create Invoice</span>
          </button>
        )}
      </header>

      <div className="lwp-convo-body" ref={scrollRef} onScroll={onScroll}>
        {loading ? (
          <Spinner />
        ) : (
          <>
            {loadingOlder && <Spinner />}
            {messages.length === 0 && <p className="lwp-convo-hint">Say hello to start the conversation.</p>}
            {messages.map((m) => {
              const day = moment(m.created_at).format("YYYY-MM-DD");
              const divider = day !== lastDay;
              lastDay = day;
              // Call records carry no sender; they sit on the lawyer's side like the app shows them.
              const mine = m.sender === "lawyer" || (!m.sender && !!m.call_data);
              return (
                <React.Fragment key={m.id}>
                  {divider && (
                    <div className="lwp-day-divider">
                      <span>{moment(m.created_at).format("DD MMM, YYYY")}</span>
                    </div>
                  )}
                  <MessageBubble
                    message={m}
                    mine={mine}
                    avatar={mine ? profile?.picture : chat?.user?.picture}
                    clientName={chat?.user?.name || "Client"}
                    busy={busyInvoice === m.id}
                    onTaskCompleted={() => markTaskCompleted(m)}
                    onReply={startReply}
                    onJumpTo={jumpTo}
                  />
                </React.Fragment>
              );
            })}
            {typing && (
              <div className="lwp-msg">
                <Avatar src={chat?.user?.picture} size={44} className="lwp-msg-avatar" />
                <div className="lwp-bubble lwp-typing" aria-label="Typing">
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {service && !serviceActive ? (
        <div className="lwp-composer">
          <div className="lwp-service-closed">
            <i className="bi bi-info-circle" />
            <span>
              {service.status === "Completed" ? "Service has been Completed" : `Service is ${service.status || "inactive"}`}
            </span>
          </div>
        </div>
      ) : (
        <form className="lwp-composer" onSubmit={handleSubmit}>
          {replyTo && (
            <div className="lwp-reply-preview">
              <i className="bi bi-reply-fill" />
              <div>
                <strong>Replying to {replyTo.sender === "lawyer" ? "yourself" : chat?.user?.name || "Client"}</strong>
                <span>{snippetOf(replyTo)}</span>
              </div>
              <button type="button" className="lwp-icon-btn" onClick={() => setReplyTo(null)} aria-label="Cancel reply">
                <i className="bi bi-x-lg" />
              </button>
            </div>
          )}
          {pendingFile && (
            <div className="lwp-pending-file">
              <i className={`bi ${fileTypeOf(pendingFile) === "image" ? "bi-image" : "bi-paperclip"}`} />
              <span>{pendingFile.name}</span>
              <button type="button" className="lwp-icon-btn" onClick={() => setPendingFile(null)} aria-label="Remove file">
                <i className="bi bi-x-lg" />
              </button>
            </div>
          )}
          <div className="lwp-composer-bar">
            {recording ? (
              <>
                <button type="button" className="lwp-icon-btn lwp-composer-icon" onClick={() => stopRecording(true)} aria-label="Cancel recording">
                  <i className="bi bi-trash3" />
                </button>
                <span className="lwp-recording">
                  <span className="lwp-rec-dot" />
                  Recording {moment.utc(recording.seconds * 1000).format("mm:ss")}
                </span>
                <button type="button" className="lwp-send-btn" onClick={() => stopRecording(false)} aria-label="Send voice message">
                  <i className="bi bi-send-fill" />
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="lwp-icon-btn lwp-composer-icon"
                  onClick={() => fileRef.current?.click()}
                  aria-label="Attach file"
                  disabled={sending}
                >
                  <i className="bi bi-paperclip" />
                </button>
                <input ref={fileRef} type="file" hidden onChange={pickFile} />
                <textarea
                  ref={inputRef}
                  rows={1}
                  className="lwp-composer-input"
                  placeholder="Type a message..."
                  value={text}
                  onChange={(e) => {
                    setText(e.target.value);
                    emitTyping();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Escape" && replyTo) {
                      setReplyTo(null);
                    } else if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                />
                {canSend ? (
                  <button type="submit" className="lwp-send-btn" disabled={sending} aria-label="Send">
                    {sending ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-send-fill" />}
                  </button>
                ) : (
                  <button type="button" className="lwp-send-btn" onClick={startRecording} disabled={sending} aria-label="Record voice message">
                    <i className="bi bi-mic-fill" />
                  </button>
                )}
              </>
            )}
          </div>
        </form>
      )}

      <CreateInvoiceModal
        open={invoiceOpen}
        onClose={() => setInvoiceOpen(false)}
        onSubmit={sendInvoice}
        commission={Number(profile?.partner_commission) || 0}
      />
      <Modal
        open={completeOpen}
        onClose={() => !completing && setCompleteOpen(false)}
        title="Mark as Completed"
        footer={
          <div className="lwp-modal-actions">
            <button type="button" className="lwp-btn lwp-btn-outline" onClick={() => setCompleteOpen(false)} disabled={completing}>
              Cancel
            </button>
            <button type="button" className="lwp-btn" onClick={markServiceCompleted} disabled={completing}>
              {completing ? <span className="spinner-border spinner-border-sm" /> : "Complete"}
            </button>
          </div>
        }
      >
        <p className="lwp-modal-text">
          Mark this service for {chat?.user?.name || "the client"} as completed? The client will be notified and the chat
          will close for new messages.
        </p>
      </Modal>
    </div>
  );
};

export default ChatConversation;
